<script>
export default {
  props: {
    // 父層可選擇傳入現有表單清單（由 GET: /admin/product/boarding/forms/search 帶入）
    boardingForms: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      // UI 列表（每筆都有自己的 draft，可同時編輯多筆）
      questions: [],
      // 是否正在儲存（單筆/全部都共用這個 loading）
      saving: false,
    }
  },
  watch: {
    // 監聽父層傳入的 forms：
    // - 立即同步到 questions（UI 用結構）
    // - 過濾 state = -2（刪除）
    // - 每筆建立自己的 draft，讓多筆同時編輯互不影響
    boardingForms: {
      immediate: true,
      deep: true,
      handler(val) {
        if (!Array.isArray(val) || val.length === 0) {
          this.questions = []
          return
        }

        this.questions = val
          .filter((f) => f && f.state !== -2)
          .map((f) => this.fromApiToUi(f))
      },
    },
  },
  methods: {
    // 透過事件交給父層打 API：
    // this.$emit('store-form', payload, resolve, reject)
    // 這裡包成 Promise，子層可用 await
    callStoreForm(payload) {
      return new Promise((resolve, reject) => {
        this.$emit('store-form', payload, resolve, reject)
      })
    },

    // 後端資料 => UI 內部資料結構
    // - 每筆都有穩定 _key（即使 id 為 null 也要唯一）
    // - 每筆都會建立 draft（編輯用副本）
    fromApiToUi(api) {
      const id = api.id ?? null
      const base = {
        _key: id != null ? String(id) : `tmp_${Date.now()}_${Math.random().toString(16).slice(2)}`,
        id,
        title: api.title || '',
        // API uses 0/1; keep numeric to avoid boolean/string casting issues
        isRequired: Number(api.isRequired) ? 1 : 0,
        isFirstOnly: Number(api.isFirstOnly) ? 1 : 0,
        type: api.type || 'text',
        options: Array.isArray(api.options) ? api.options : [],
        hasOtherOption: false,
        isExpanded: false,
        isNew: !api.id,
        state: typeof api.state === 'number' ? api.state : 1,
      }

      return {
        ...base,
        // draft：編輯用（可同時編輯多筆）
        draft: JSON.parse(JSON.stringify(base)),
      }
    },

    // UI 資料(draft) => 後端 API payload
    // - 會 trim title/options
    // - type 為 single/multiple 時 options 必填；text 時 options = null
    // - 可用 overrides 覆蓋（例如 state: -2）
    toApiPayload(draft, overrides = {}) {
      const apiType = draft.type || 'text'
      const needsOptions = apiType === 'single' || apiType === 'multiple'
      const cleanedOptions = Array.isArray(draft.options)
        ? draft.options
            .map((s) => (typeof s === 'string' ? s.trim() : ''))
            .filter(Boolean)
        : []

      return {
        id: draft.id ?? null,
        title: (draft.title || '').trim(),
        type: apiType,
        // API uses 0/1
        isFirstOnly: Number(draft.isFirstOnly) ? 1 : 0,
        isRequired: Number(draft.isRequired) ? 1 : 0,
        state: typeof draft.state === 'number' ? draft.state : 1,
        options: needsOptions ? cleanedOptions : null,
        ...overrides,
      }
    },

    // 新增一筆題目（前端先生成，尚未存到後端）
    // - 產生唯一 _key（避免 null id 重複）
    // - 每筆都有自己的 draft，可同時編輯多筆
    addQuestion() {
      const base = {
        _key: `tmp_${Date.now()}_${Math.random().toString(16).slice(2)}`,
        id: null,
        title: '',
        isRequired: 1,
        isFirstOnly: 1,
        type: 'text',
        options: ['', ''],
        hasOtherOption: false,
        isExpanded: true,
        isNew: true,
        state: 1,
      }

      this.questions.unshift({
        ...base,
        draft: JSON.parse(JSON.stringify(base)),
      })
    },

    // 展開/收合指定題目
    // - 允許多筆同時展開（多筆同時編輯）
    editQuestion(question) {
      question.isExpanded = !question.isExpanded

      // 如果展開時 draft 不存在或被清掉，補一份（保險）
      if (question.isExpanded && !question.draft) {
        const cloned = {
          ...question,
        }
        delete cloned.draft
        delete cloned.dirty
        question.draft = JSON.parse(JSON.stringify(cloned))
      }
    },

    // 切換回答形式時，調整 options
    // - text：清空 options
    // - single/multiple：若沒有 options，補預設兩個空字串
    onTypeChanged(question) {
      this.ensureDraft(question)
      const apiType = question?.draft?.type || 'text'
      if (apiType === 'text') {
        question.draft.options = []
      } else if (!Array.isArray(question.draft.options) || question.draft.options.length === 0) {
        question.draft.options = ['', '']
      }
      this.syncMainFromDraft(question)
    },

    // 新增一個選項（僅 single/multiple 會用到）
    addOption(question) {
      if (!question?.draft) return
      if (!Array.isArray(question.draft.options)) question.draft.options = []
      question.draft.options.push('')
    },

    // 移除指定 index 的選項
    removeOption(question, index) {
      if (!question?.draft || !Array.isArray(question.draft.options)) return
      question.draft.options.splice(index, 1)
    },

    // 單筆儲存：只儲存該筆 question（用 question.draft 組 payload）
    // - title 必填
    // - single/multiple 時至少 1 個非空選項
    // - 成功後用後端回傳覆蓋主資料並重建 draft
    async saveQuestion(question) {
      if (!question?.draft) return

      const payload = this.toApiPayload(question.draft)
      if (!payload.title) {
        this.$message?.error?.('請輸入問題標題')
        return
      }

      if ((payload.type === 'single' || payload.type === 'multiple') && (!payload.options || payload.options.length < 1)) {
        this.$message?.error?.('請至少輸入 1 個選項')
        return
      }

      this.saving = true
      try {
        const res = await this.callStoreForm(payload)
        const returned = res?.data?.data || res?.data || null

        // 取得後端回傳（最重要是 id）
        if (returned && typeof returned === 'object') {
          const ui = this.fromApiToUi(returned)
          const keepKey = question._key

          Object.assign(question, ui)
          question._key = keepKey
        } else {
          // 若後端沒有回傳資料，至少把 draft 同步到主資料
          const keepKey = question._key
          Object.assign(question, JSON.parse(JSON.stringify(question.draft)))
          question._key = keepKey

          // 重建 draft
          const cloned = { ...question }
          delete cloned.draft
          question.draft = JSON.parse(JSON.stringify(cloned))
        }

        question.isExpanded = false
        question.isNew = false
        this.$message?.success?.('已儲存')
      } catch (e) {
        console.error(e)
        this.$message?.error?.('儲存失敗')
      } finally {
        this.saving = false
      }
    },

    // 取消編輯（單筆）：
    // - 若是新增未存過（沒有 id），直接移除
    // - 否則把 draft 回滾成目前主資料
    cancelEdit(question) {
      if (!question) return

      // 新增尚未存過：直接移除
      if (!question.id) {
        this.questions = this.questions.filter((q) => q !== question)
        return
      }

      // 回滾 draft
      const cloned = { ...question }
      delete cloned.draft
      question.draft = JSON.parse(JSON.stringify(cloned))
      question.isExpanded = false
    },

    // 刪除題目
    // - 未存過（沒有 id）：直接從列表移除
    // - 已存過：呼叫 API 送 state: -2 做軟刪除，成功後再移除
    async deleteQuestion(question) {
      await this.$confirm('確定要刪除此題目嗎？', '提示', {
          confirmButtonText: '確定',
          cancelButtonText: '取消',
          type: 'warning',
        })

      const target = this.questions.find((q) => q._key === question?._key)
      if (!target) return

      // 新增尚未存過：直接移除
      if (!target.id) {
        this.questions = this.questions.filter((q) => q !== target)
        return
      }

      this.saving = true
      try {
        const payload = this.toApiPayload(target.draft || target, { state: -2 })
        await this.callStoreForm(payload)

        this.questions = this.questions.filter((q) => q !== target)
        this.$message?.success?.('已刪除')
      } catch (e) {
        console.error(e)
        this.$message?.error?.('刪除失敗')
      } finally {
        this.saving = false
      }
    },

    // 儲存全部：逐筆呼叫 API（API 只支援單筆，所以用迴圈）
    // - 不再判斷是否有編輯過（不使用 dirty）
    // - 不中斷：某筆失敗不影響其他筆
    async saveAll() {
      // 先驗證：有無未填寫完整的題目（排除 state = -2 被刪除的題目）
      const invalid = []
      for (const q of this.questions) {
        const state = (q.draft && typeof q.draft.state === 'number') ? q.draft.state : q.state
        if (state === -2) continue
        const payload = this.toApiPayload(q.draft || q)
        if (!payload.title || !String(payload.title).trim()) {
          invalid.push({ key: q._key, reason: 'title' })
          continue
        }
        if ((payload.type === 'single' || payload.type === 'multiple') && (!payload.options || payload.options.length < 1)) {
          invalid.push({ key: q._key, reason: 'options' })
        }
      }

      if (invalid.length) {
        this.$message?.error?.('有題目未填寫完整，請補齊後再儲存')
        return
      }

      this.saving = true
      const failed = []
      let successCount = 0

      try {
        for (const q of this.questions) {
          // 跳過已標記刪除
          const state = (q.draft && typeof q.draft.state === 'number') ? q.draft.state : q.state
          if (state === -2) continue

          const payload = this.toApiPayload(q.draft || q)

          // 跳過不完整（避免打壞資料）
          if (!payload.title) continue
          if ((payload.type === 'single' || payload.type === 'multiple') && (!payload.options || payload.options.length < 1)) continue

          try {
            const res = await this.callStoreForm(payload)
            const returned = res?.data?.data || res?.data || null

            if (returned && typeof returned === 'object') {
              const ui = this.fromApiToUi(returned)
              const keepKey = q._key
              Object.assign(q, ui)
              q._key = keepKey
            }

            q.isNew = false
            q.isExpanded = false
            successCount += 1
          } catch (err) {
            console.error('store failed:', payload, err)
            failed.push({ title: payload.title, err })
          }
        }

        if (failed.length === 0) {
          this.$message?.success?.('儲存成功')
          this.$emit('saved')
        } else {
          this.$message?.warning?.(`已儲存 ${successCount} 筆，失敗 ${failed.length} 筆（請看 console）`)
        }
      } finally {
        this.saving = false
      }
    },

    // 取消全部：以父層 boardingForms 重新覆蓋 questions
    cancelAll() {
      if (Array.isArray(this.boardingForms) && this.boardingForms.length > 0) {
        this.questions = this.boardingForms
          .filter((f) => f && f.state !== -2)
          .map((f) => this.fromApiToUi(f))
      } else {
        this.questions = []
      }
      this.$message?.info?.('已取消')
    },

    // 確保 question.draft 一定存在（避免未展開時 draft 不存在造成 v-model / 儲存不同步）
    ensureDraft(question) {
      if (!question) return
      if (question.draft) return

      const cloned = { ...question }
      delete cloned.draft
      question.draft = JSON.parse(JSON.stringify(cloned))
    },

    // 未展開（View Mode）直接切換時，把「主資料」同步到 draft，確保儲存 payload 正確
    syncDraftField(question, field) {
      if (!question) return
      this.ensureDraft(question)
      question.draft[field] = question[field]
    },

    // 同步：把 draft 的欄位回寫到主資料，確保列表顯示即時更新
    syncMainFromDraft(question) {
      if (!question) return
      this.ensureDraft(question)
      if (!question.draft) return

      if (typeof question.draft.title !== 'undefined') question.title = question.draft.title
      if (typeof question.draft.type !== 'undefined') question.type = question.draft.type
      if (typeof question.draft.isFirstOnly !== 'undefined') question.isFirstOnly = question.draft.isFirstOnly
      if (typeof question.draft.isRequired !== 'undefined') question.isRequired = question.draft.isRequired
    },
  },
}
</script>

<template>
  <div :class="$style.container">
    <div :class="$style.header">
      <div :class="$style.title">題目設定</div>
      <el-button type="primary" icon="el-icon-plus" @click="addQuestion" class="is-round">新增題目</el-button>
    </div>
    <div :class="$style.questionList">
      <div v-for="question in questions" :key="question._key" :class="$style.questionItem">
        <!-- View Mode -->
        <div v-if="!question.isExpanded" :class="$style.questionSummary" @click="editQuestion(question)">
          <div :class="$style.summaryLeft">
            <i :class="['el-icon-arrow-right', $style.arrowIcon]"></i>
            <span :class="$style.questionTitle">{{ (question.draft && question.draft.title) || question.title || '新題目' }}</span>
          </div>
          <div :class="$style.summaryRight">
            <span :class="$style.typeTag">
              {{
                ((question.draft && question.draft.type) || question.type) === 'text'
                  ? '文字輸入'
                  : ((question.draft && question.draft.type) || question.type) === 'single'
                  ? '單選'
                  : '多選'
              }}
            </span>
            <div :class="$style.requiredToggle" @click.stop>
              <span :class="[$style.statusText, { [$style.active]: !question.isRequired }]">非必填</span>
              <el-switch
                v-model="question.isRequired"
                :active-value="1"
                :inactive-value="0"
                active-color="#F37736"
                inactive-color="#EAEAEA"
                @change="syncDraftField(question, 'isRequired')"
              >
              </el-switch>
              <span :class="[$style.statusText, { [$style.active]: question.isRequired }]">必填</span>
            </div>
          </div>
        </div>

        <!-- Edit Mode -->
        <div v-else :class="$style.editForm">
          <div :class="$style.formHeader">
            <div :class="$style.summaryLeft" @click="editQuestion(question)">
              <i :class="['el-icon-arrow-down', $style.arrowIcon]"></i>
              <span :class="$style.questionTitle">{{ (question.draft && question.draft.title) || '新題目' }}</span>
            </div>
            <div :class="$style.summaryRight">
              <span :class="$style.typeTag">
                {{
                  (question.draft.type === 'text')
                    ? '文字輸入'
                    : (question.draft.type === 'single')
                    ? '單選'
                    : '多選'
                }}
              </span>
              <div :class="$style.requiredToggle">
                <span :class="[$style.statusText, { [$style.active]: !question.draft.isRequired }]">非必填</span>
                <el-switch
                  v-model="question.draft.isRequired"
                  :active-value="1"
                  :inactive-value="0"
                  active-color="#F37736"
                  inactive-color="#EAEAEA"
                  @change="question.isRequired = question.draft.isRequired; syncMainFromDraft(question)"
                ></el-switch>
                <span :class="[$style.statusText, { [$style.active]: question.draft.isRequired }]">必填</span>
              </div>
            </div>
          </div>

          <div :class="$style.formContent">
            <div :class="$style.formGroup">
              <label>問題類型 <span :class="$style.required">*</span></label>
              <div :class="$style.inputGroup">
                <el-radio-group v-model="question.draft.isFirstOnly" :class="$style.questionTypeGroup">
                  <el-radio :label="1" :class="[$style.questionTypeRadio, { [$style.active]: Number(question.draft.isFirstOnly) === 1 }]">初次預約時才需填寫</el-radio>
                  <el-radio :label="0" :class="[$style.questionTypeRadio, { [$style.active]: Number(question.draft.isFirstOnly) === 0 }]">每次預約需填寫</el-radio>
                </el-radio-group>
              </div>
            </div>

            <div :class="$style.formRow">
              <div :class="$style.formGroup" style="flex: 2">
                <label>問題標題 <span :class="$style.required">*</span></label>
                <el-input v-model="question.draft.title" placeholder="請輸入問題標題" @input="syncMainFromDraft(question)"></el-input>
              </div>
              <div :class="$style.formGroup" style="flex: 1">
                <label>回答形式 <span :class="$style.required">*</span></label>
                <el-select v-model="question.draft.type" placeholder="請選擇" @change="onTypeChanged(question)">
                  <el-option label="文字輸入" value="text"></el-option>
                  <el-option label="單選" value="single"></el-option>
                  <el-option label="多選" value="multiple"></el-option>
                </el-select>
              </div>
            </div>

            <!-- Options Section -->
            <template v-if="question.draft.type !== 'text'">
              <div v-for="(option, index) in question.draft.options" :key="index" :class="$style.optionRow">
                <div :class="$style.optionLabel"> <i class="el-icon-menu"></i> 選項{{ index + 1 }} <span :class="$style.required">*</span> </div>
                <div :class="$style.optionInput">
                  <el-input v-model="question.draft.options[index]" placeholder="請輸入選項內容" @input="syncMainFromDraft(question)"></el-input>
                  <i class="el-icon-close" @click="removeOption(question, index)" v-if="question.draft.options.length > 1"></i>
                </div>
              </div>

              <div :class="$style.optionActions">
                <el-button type="primary" icon="el-icon-plus" @click="addOption(question)" class="is-round">新增選項</el-button>
                <!-- <el-button type="warning" icon="el-icon-plus" @click="addOption">新增選項</el-button> -->
                <!-- <div :class="$style.otherOption">
                  <span>或</span>
                  <el-button plain @click="question.draft.hasOtherOption = !question.draft.hasOtherOption; syncMainFromDraft(question)">
                    {{ question.draft.hasOtherOption ? '移除『其他』輸入框' : '新增『其他』輸入框' }}
                  </el-button>
                </div> -->
              </div>
            </template>

            <div :class="$style.formActions">
              <el-button plain @click="deleteQuestion(question)" :class="$style.deleteBtn" class="is-round">刪除題目</el-button>
              <!-- <el-button plain @click="cancelEdit(question)">取消</el-button> -->
              <!-- <el-button type="primary" :loading="saving" @click="saveQuestion(question)">儲存題目</el-button> -->
            </div>
          </div>
        </div>
      </div>
    </div>

    <div :class="$style.footer">
      <el-button @click="cancelAll" class="is-round">取消編輯</el-button>
      <el-button type="primary" :loading="saving" @click="saveAll" class="is-round">儲存編輯</el-button>
    </div>
  </div>
</template>

<style lang="scss" module>
.container {
  padding: 20px;
  background-color: #fff;
  min-height: 100%;
  position: relative;
  padding-bottom: 80px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.questionList {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.questionItem {
  border: 1px solid #eaeaea;
  border-radius: 4px;
  background-color: #fff;
  overflow: hidden;
}

.questionSummary {
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  background-color: #fafafa;

  &:hover {
    background-color: #f5f7fa;
  }
}

.formHeader {
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #eaeaea;
  background-color: #fafafa;
}

.summaryLeft {
  display: flex;
  align-items: center;
  gap: 10px;
}

.arrowIcon {
  color: #909399;
  font-size: 14px;
}

.questionTitle {
  font-size: 15px;
  color: #333;
  font-weight: 500;
}

.summaryRight {
  display: flex;
  align-items: center;
  gap: 20px;
}

.typeTag {
  color: #909399;
  font-size: 14px;
}

.requiredToggle {
  display: flex;
  align-items: center;
}

.statusText {
  margin: 0 10px;
  color: #999;
  font-size: 14px;

  &.active {
    color: #f37736;
  }
}

.editForm {
  background-color: #fff;
}

.formContent {
  padding: 20px;
}

.formGroup {
  margin-bottom: 20px;

  label {
    display: block;
    margin-bottom: 10px;
    color: #606266;
    font-size: 14px;
  }
}

.formRow {
  display: flex;
  gap: 20px;
}

.required {
  color: #f56c6c;
}

.inputGroup {
  display: flex;
  align-items: center;
  // height: 40px; // Remove fixed height to allow expansion
}

.questionTypeGroup {
  display: flex;
  gap: 20px;
  width: 100%;
}

.questionTypeRadio {
  flex: 1;
  margin-right: 0 !important;
  border: 1px solid #eaeaea;
  border-radius: 4px;
  padding: 15px 20px;
  display: flex;
  align-items: center;
  transition: all 0.3s;

  &.active {
    border-color: #f37736;
  }

  // Customizing Element UI radio styles
  :global {
    .el-radio__input.is-checked .el-radio__inner {
      border-color: #f37736;
      background: #f37736;
    }

    .el-radio__input.is-checked + .el-radio__label {
      color: #333;
    }

    .el-radio__label {
      font-size: 14px;
      color: #333;
    }
  }
}

.optionRow {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  gap: 10px;
}

.optionLabel {
  width: 80px;
  color: #f37736;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.optionInput {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;

  i {
    cursor: pointer;
    color: #909399;
    font-size: 16px;

    &:hover {
      color: #f56c6c;
    }
  }
}

.optionActions {
  display: flex;
  align-items: center;
  margin-top: 10px;
  margin-bottom: 30px;

  button {
    border-radius: 4px;
  }
}

.otherOption {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: 10px;

  span {
    color: #606266;
  }
}

.formActions {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #eaeaea;

  button {
    flex: 1;
    border-radius: 4px;
  }
}

.deleteBtn {
  color: #606266;
  border-color: #dcdfe6;
  /* 讓刪除按鈕只佔 formActions 區塊的 50% */
  flex: 0 0 50%;
  max-width: 50%;

  &:hover {
    color: #f56c6c;
    border-color: #f56c6c;
    background-color: #fef0f0;
  }
}

.footer {
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.14);
  display: flex;
  justify-content: center;
  position: fixed;
  bottom: 0;
  left: $size-sidebar-width;
  z-index: $layer-page-footer-z-index;
  width: calc(100% - #{$size-sidebar-width});
  padding: 20px 0;
  padding-left: 5%;
  padding-right: 5%;
  text-align: center;
  background-color: white;

  > * {
    flex: 1 0 0;
  }
}
</style>
